import { useEffect, useRef, useState } from "react";
import { useThread } from "@assistant-ui/react";
import { useCreateChatHistory, useUpdateChatHistory } from "@acto/taskpane/core/data-access";
import { convertMessagesToStorageFormat } from "./chatMessageHelpers";

interface UseChatPersistenceProps {
  customerId?: string;
  sessionId?: string;
}

export function useChatPersistence({ customerId, sessionId }: UseChatPersistenceProps) {
  const thread = useThread();
  const { createChatHistory } = useCreateChatHistory();
  const { updateChatHistory } = useUpdateChatHistory();

  const [chatHistoryId, setChatHistoryId] = useState<string | null>(null);
  const lastMessageCountRef = useRef(0);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (!customerId || !sessionId) {
      return;
    }

    const messages = thread.messages;
    const currentMessageCount = messages.length;

    if (currentMessageCount === lastMessageCountRef.current) {
      return;
    }

    if (thread.isRunning || isSavingRef.current) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.role !== "assistant") {
      return;
    }

    lastMessageCountRef.current = currentMessageCount;

    const saveChatHistory = async () => {
      if (isSavingRef.current) return;

      isSavingRef.current = true;
      try {
        const messagesJson = convertMessagesToStorageFormat(messages);

        if (!chatHistoryId) {
          const result = await createChatHistory({
            clientId: customerId,
            sessionId,
            messages: messagesJson,
          });

          if (result?.id) {
            setChatHistoryId(result.id);
          }
        } else {
          await updateChatHistory({
            id: chatHistoryId,
            sessionId,
            messages: messagesJson,
          });
        }
      } catch (error) {
        console.error("Failed to persist chat history:", error);
      } finally {
        isSavingRef.current = false;
      }
    };

    saveChatHistory();
  }, [customerId, sessionId, thread.messages, thread.isRunning, createChatHistory, updateChatHistory, chatHistoryId]);

  return {
    chatHistoryId,
  };
}
